/**
 * Procedural audio.
 *
 * Everything here is synthesised at runtime: no samples, no loading, nothing to ship.
 * That is partly practical — a prototype should not carry a megabyte of audio it will
 * throw away — and partly because parameterised sound is the point. A wall hit is louder
 * and brighter when the ball arrives faster; the fluid's hiss follows how fast it is
 * actually moving; the magnet hum tracks how many magnets are on the board. Sound that
 * responds to the simulation reads as production value in a way a fixed sample does not.
 *
 * Browsers refuse to start audio without a gesture, so the context is created lazily on
 * the first touch and everything before that is a no-op.
 */

/** Master level. Individual voices are balanced against this, not against each other. */
const MASTER_GAIN = 0.5;

const STORAGE_KEY = 'magneto.muted';

interface Bed {
  gain: GainNode;
  filter: BiquadFilterNode;
}

export class AudioEngine {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private noiseBuffer: AudioBuffer | null = null;

  private fluidBed: Bed | null = null;
  private magnetBed: Bed | null = null;

  private muted = false;
  /** Smoothed inputs, so the beds do not chatter when the simulation does. */
  private fluidLevel = 0;
  private magnetLevel = 0;

  constructor() {
    try {
      this.muted = localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      // Private mode, or storage disabled. Not worth caring about.
      this.muted = false;
    }
  }

  get isMuted(): boolean {
    return this.muted;
  }

  get started(): boolean {
    return this.context !== null;
  }

  /**
   * Called from the first user gesture. Safe to call repeatedly — a context suspended by
   * the browser (tab switch, phone call) gets resumed here too.
   */
  unlock(): void {
    if (!this.context) {
      const Constructor =
        window.AudioContext ??
        (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Constructor) return;

      try {
        this.context = new Constructor();
      } catch {
        return;
      }
      this.build();
    }
    if (this.context.state === 'suspended') void this.context.resume();
  }

  setMuted(muted: boolean): void {
    this.muted = muted;
    try {
      localStorage.setItem(STORAGE_KEY, muted ? '1' : '0');
    } catch {
      // Nothing to do; the preference just will not persist.
    }
    if (this.master && this.context) {
      this.master.gain.setTargetAtTime(muted ? 0 : MASTER_GAIN, this.context.currentTime, 0.05);
    }
  }

  private build(): void {
    const context = this.context;
    if (!context) return;

    // A gentle limiter, so a pile-up of ball hits cannot clip.
    const compressor = context.createDynamicsCompressor();
    compressor.threshold.value = -14;
    compressor.knee.value = 22;
    compressor.ratio.value = 8;
    compressor.attack.value = 0.004;
    compressor.release.value = 0.18;
    compressor.connect(context.destination);

    this.master = context.createGain();
    this.master.gain.value = this.muted ? 0 : MASTER_GAIN;
    this.master.connect(compressor);

    this.noiseBuffer = createNoiseBuffer(context);

    // Room tone: a low drone plus a whisper of filtered noise, both barely audible on
    // their own. Silence between events makes a game feel unfinished.
    const droneGain = context.createGain();
    droneGain.gain.value = 0.05;
    droneGain.connect(this.master);
    for (const [frequency, level] of [
      [55, 1],
      [82.5, 0.5],
      [110, 0.25],
    ] as const) {
      const oscillator = context.createOscillator();
      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;
      const gain = context.createGain();
      gain.gain.value = level;
      oscillator.connect(gain).connect(droneGain);
      oscillator.start();
    }

    const air = context.createBufferSource();
    air.buffer = this.noiseBuffer;
    air.loop = true;
    const airFilter = context.createBiquadFilter();
    airFilter.type = 'bandpass';
    airFilter.frequency.value = 700;
    airFilter.Q.value = 0.6;
    const airGain = context.createGain();
    airGain.gain.value = 0.012;
    air.connect(airFilter).connect(airGain).connect(this.master);
    air.start();

    // Fluid bed: noise through a bandpass whose gain and brightness follow how fast the
    // ferrofluid is moving. Slow blob, low murmur; a blob snapping to a magnet, a rush.
    this.fluidBed = this.createNoiseBed(320, 1.4, 'bandpass');

    // Magnet bed: a quiet electrical hum that appears when magnets are on the board.
    const hum = context.createOscillator();
    hum.type = 'sawtooth';
    hum.frequency.value = 60;
    const humFilter = context.createBiquadFilter();
    humFilter.type = 'lowpass';
    humFilter.frequency.value = 260;
    humFilter.Q.value = 6;
    const humGain = context.createGain();
    humGain.gain.value = 0;
    hum.connect(humFilter).connect(humGain).connect(this.master);
    hum.start();
    this.magnetBed = { gain: humGain, filter: humFilter };
  }

  private createNoiseBed(frequency: number, q: number, type: BiquadFilterType): Bed | null {
    const context = this.context;
    if (!context || !this.noiseBuffer || !this.master) return null;

    const source = context.createBufferSource();
    source.buffer = this.noiseBuffer;
    source.loop = true;
    const filter = context.createBiquadFilter();
    filter.type = type;
    filter.frequency.value = frequency;
    filter.Q.value = q;
    const gain = context.createGain();
    gain.gain.value = 0;
    source.connect(filter).connect(gain).connect(this.master);
    source.start();

    return { gain, filter };
  }

  /**
   * Feed the continuous beds. `fluidSpeed` is mean particle speed in world units per
   * second; `magnets` is how many are on the board.
   */
  update(dt: number, fluidSpeed: number, magnets: number): void {
    const context = this.context;
    if (!context) return;

    // Smooth the inputs here rather than relying on setTargetAtTime alone, so a single
    // violent tick cannot spike the level.
    const target = Math.min(1, fluidSpeed / 420);
    const rate = 1 - Math.exp(-dt * 6);
    this.fluidLevel += (target - this.fluidLevel) * rate;
    this.magnetLevel += (Math.min(1, magnets / 3) - this.magnetLevel) * rate;

    const now = context.currentTime;
    if (this.fluidBed) {
      this.fluidBed.gain.gain.setTargetAtTime(this.fluidLevel * 0.16, now, 0.08);
      this.fluidBed.filter.frequency.setTargetAtTime(320 + this.fluidLevel * 1500, now, 0.08);
    }
    if (this.magnetBed) {
      this.magnetBed.gain.gain.setTargetAtTime(this.magnetLevel * 0.035, now, 0.15);
    }
  }

  // --- one-shots -----------------------------------------------------------

  /** Magnet placed: a weighty clunk plus a rising confirmation blip. */
  magnetPlace(): void {
    this.thud(120, 0.22, 0.5);
    this.blip(420, 760, 0.16, 0.12, 'triangle');
  }

  /** Polarity flipped: a resonant sweep, upwards for positive and down for negative. */
  magnetFlip(polarity: number): void {
    const context = this.context;
    if (!context || !this.master) return;
    const now = context.currentTime;

    const oscillator = context.createOscillator();
    oscillator.type = 'sawtooth';
    oscillator.frequency.setValueAtTime(polarity > 0 ? 180 : 420, now);
    oscillator.frequency.exponentialRampToValueAtTime(polarity > 0 ? 460 : 150, now + 0.18);

    const filter = context.createBiquadFilter();
    filter.type = 'lowpass';
    filter.Q.value = 12;
    filter.frequency.setValueAtTime(600, now);
    filter.frequency.exponentialRampToValueAtTime(polarity > 0 ? 2400 : 400, now + 0.18);

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(0.2, now + 0.012);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.26);

    oscillator.connect(filter).connect(gain).connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + 0.3);
  }

  /** Magnet removed: a falling blip and a click of the mount letting go. */
  magnetRemove(): void {
    this.blip(520, 190, 0.2, 0.11, 'triangle');
    this.noiseBurst(1800, 3, 0.06, 0.1);
  }

  /** Ball against a wall. Louder, brighter and higher the faster it arrives. */
  ballImpact(speed: number): void {
    const strength = Math.min(1, speed / 900);
    if (strength < 0.05) return;
    this.thud(150 + strength * 120, 0.12 + strength * 0.08, 0.18 + strength * 0.45);
    this.noiseBurst(900 + strength * 2600, 1.6, 0.05 + strength * 0.05, 0.06 + strength * 0.18);
  }

  /** Level solved: a rising arpeggio with a long tail. */
  goal(): void {
    const context = this.context;
    if (!context) return;
    // A major add-9 shape, spread over a third of a second.
    const notes = [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((frequency, index) => {
      this.tone(frequency, 0.9, 0.14, 'triangle', index * 0.075);
      this.tone(frequency * 2, 0.7, 0.05, 'sine', index * 0.075 + 0.01);
    });
    this.noiseBurst(5200, 0.8, 0.5, 0.05);
  }

  /** A run is committed: a rising two-note cue and a low swell as the field takes hold. */
  runStart(): void {
    this.blip(300, 620, 0.22, 0.13, 'triangle');
    this.tone(196, 0.5, 0.09, 'sine', 0.02);
    this.noiseBurst(700, 1.2, 0.22, 0.07);
  }

  /** A run that did not get there. A soft falling pair, not a buzzer. */
  runFailed(): void {
    this.tone(349.23, 0.4, 0.09, 'triangle', 0);
    this.tone(261.63, 0.6, 0.09, 'triangle', 0.12);
  }

  /** New level: a short two-note announcement. */
  levelStart(): void {
    this.tone(392, 0.45, 0.08, 'triangle', 0);
    this.tone(587.33, 0.5, 0.09, 'triangle', 0.1);
  }

  uiClick(): void {
    this.noiseBurst(2600, 2.5, 0.04, 0.07);
    this.blip(660, 660, 0.05, 0.05, 'sine');
  }

  // --- voices --------------------------------------------------------------

  /** Low-frequency body: what makes a hit feel like it had mass. */
  private thud(frequency: number, duration: number, level: number): void {
    const context = this.context;
    if (!context || !this.master) return;
    const now = context.currentTime;

    const oscillator = context.createOscillator();
    oscillator.type = 'sine';
    oscillator.frequency.setValueAtTime(frequency, now);
    oscillator.frequency.exponentialRampToValueAtTime(frequency * 0.45, now + duration);

    const gain = context.createGain();
    gain.gain.setValueAtTime(level, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    oscillator.connect(gain).connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }

  private blip(
    from: number,
    to: number,
    duration: number,
    level: number,
    type: OscillatorType,
  ): void {
    const context = this.context;
    if (!context || !this.master) return;
    const now = context.currentTime;

    const oscillator = context.createOscillator();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(from, now);
    oscillator.frequency.exponentialRampToValueAtTime(Math.max(to, 1), now + duration);

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, now);
    gain.gain.exponentialRampToValueAtTime(level, now + 0.008);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    oscillator.connect(gain).connect(this.master);
    oscillator.start(now);
    oscillator.stop(now + duration + 0.02);
  }

  private tone(
    frequency: number,
    duration: number,
    level: number,
    type: OscillatorType,
    delay: number,
  ): void {
    const context = this.context;
    if (!context || !this.master) return;
    const start = context.currentTime + delay;

    const oscillator = context.createOscillator();
    oscillator.type = type;
    oscillator.frequency.value = frequency;

    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(level, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);

    oscillator.connect(gain).connect(this.master);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.02);
  }

  /** Filtered noise transient: the attack that makes a thud sound like a collision. */
  private noiseBurst(frequency: number, q: number, duration: number, level: number): void {
    const context = this.context;
    if (!context || !this.master || !this.noiseBuffer) return;
    const now = context.currentTime;

    const source = context.createBufferSource();
    source.buffer = this.noiseBuffer;
    // Start somewhere random in the buffer so repeated hits are not identical.
    source.loopStart = 0;

    const filter = context.createBiquadFilter();
    filter.type = 'bandpass';
    filter.frequency.value = frequency;
    filter.Q.value = q;

    const gain = context.createGain();
    gain.gain.setValueAtTime(level, now);
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);

    source.connect(filter).connect(gain).connect(this.master);
    source.start(now, Math.random() * 1.5, duration + 0.02);
  }
}

function createNoiseBuffer(context: AudioContext): AudioBuffer {
  const length = Math.floor(context.sampleRate * 2);
  const buffer = context.createBuffer(1, length, context.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < length; i++) data[i] = Math.random() * 2 - 1;
  return buffer;
}
